import CryptoGatewayConfig from './CryptoGatewayConfig';
import ConfigTabTemplate from './ConfigTabTemplate';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Stack, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { MarketplaceConfigFormData, marketplaceConfigSchema } from '../../schema';
import FormFieldStack from '../atoms/FormFieldStack';
import Switch from '../atoms/Switch';
import FormSelect from '../molecules/FormSelect';
import { CURRENCY_OPTIONS } from '../../constants';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useMemo, useEffect } from 'react';
import { SelectOption } from '../../@types';
import CollapsibleSection from '../atoms/CollapsibleSection';
import PartnerSelect from '../molecules/PartnerSelect';

type LanguageOption = {
  key: string;
  label: string;
};

type LanguageConfig = LanguageOption[];

const DEFAULT_VALUES: MarketplaceConfigFormData = {
  model: 'AGENT_SYSTEM',
  paymentGateway: undefined,
  cryptoProvider: 'moralis',
  moralis: { depositEnabled: true, withdrawalEnabled: true },
  zeroXProcessing: { enabled: false, depositEnabled: false, withdrawalEnabled: false },
  products: { sportsbook: false, casino: false },
  features: { paymentSettings: false },
  currency: 'EUR',
  languageConfig: {
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
  },
  licenseInformation: null,
  defaultRegistrationAgentId: null,
  registrationFlow: { signUpFormFields: [], emailVerification: 'none' },
  googleAnalyticsId: null,
  openReplayProjectKey: null,
  maintenance: {
    underMaintenance: false,
    message: {},
  },
  systems: {
    affiliate: false,
  },
  kyc: {
    enabled: false,
  },
};

const resolveInitialValues = (data: Partial<MarketplaceConfigFormData> | undefined): MarketplaceConfigFormData => ({
  ...DEFAULT_VALUES,
  ...data,
  moralis: { ...DEFAULT_VALUES.moralis, ...data?.moralis },
  zeroXProcessing: { ...DEFAULT_VALUES.zeroXProcessing, ...data?.zeroXProcessing },
});

const KYC_TRIGGER_OPTIONS: SelectOption[] = [
  { id: 'registration', name: 'Registration' },
  { id: 'firstDeposit', name: 'First Deposit' },
  { id: 'firstWithdrawal', name: 'First Withdrawal' },
];

type SignUpFieldOption = {
  id: string;
  name: string;
  order: number;
  mandatory?: boolean;
};

type RegistrationFlowOptions = {
  formFieldsOptions: SignUpFieldOption[];
  emailVerificationOptions: SelectOption[];
};

type MessageFieldName = `maintenance.message.${string}`;

const MarketplaceConfigFields = () => {
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<MarketplaceConfigFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'registrationFlow.signUpFormFields',
  });

  const authModelOptions = useFeatureValue<SelectOption[]>(FEATURE_FLAG.AUTH_MODEL_OPTIONS, []);

  const registrationOptions = useFeatureValue<RegistrationFlowOptions>(FEATURE_FLAG.REGISTRATION_FLOW_CONFIG, {
    formFieldsOptions: [],
    emailVerificationOptions: [],
  });

  const sortedFieldOptions = useMemo(
    () => [...registrationOptions.formFieldsOptions].sort((a, b) => a.order - b.order),
    [registrationOptions.formFieldsOptions]
  );

  const languageOptions = useFeatureValue<LanguageConfig>(FEATURE_FLAG.LANGUAGE_OPTIONS, []);

  const availableDefaultLanguages = languageOptions.map((language) => ({
    id: language.key,
    name: language.label,
  }));

  const paymentGatewayOptions = useFeatureValue<SelectOption[]>(FEATURE_FLAG.PAYMENT_GATEWAY_OPTIONS, []);

  const supportedLanguages = watch('languageConfig.supportedLanguages');
  const defaultLanguage = watch('languageConfig.defaultLanguage');
  const kycEnabled = watch('kyc.enabled');
  const cryptoSelected = watch('paymentGateway') === 'CRYPTO';

  useEffect(() => {
    if (defaultLanguage && supportedLanguages && !supportedLanguages.includes(defaultLanguage)) {
      setValue('languageConfig.defaultLanguage', '');
    }
  }, [supportedLanguages, defaultLanguage, setValue]);

  const filteredDefaultLanguageOptions = useMemo(() => {
    if (!supportedLanguages || supportedLanguages.length === 0) {
      return availableDefaultLanguages;
    }
    return availableDefaultLanguages.filter((lang) => supportedLanguages.includes(lang.id));
  }, [supportedLanguages, availableDefaultLanguages]);

  return (
    <Stack spacing={2}>
      <FormSelect
        label='Model'
        name='model'
        control={control}
        error={errors.model}
        options={authModelOptions}
        required
      />
      <FormSelect
        label='Payment Gateway'
        name='paymentGateway'
        control={control}
        error={errors.paymentGateway}
        options={paymentGatewayOptions}
      />
      {cryptoSelected && <CryptoGatewayConfig />}

      <FormSelect
        label='Currency'
        name='currency'
        control={control}
        error={errors.currency}
        options={CURRENCY_OPTIONS}
        required
      />
      <FormSelect
        label='Language'
        name='languageConfig.defaultLanguage'
        control={control}
        error={errors.languageConfig?.defaultLanguage}
        options={filteredDefaultLanguageOptions}
        required
      />
      <FormSelect
        label='Supported languages'
        name='languageConfig.supportedLanguages'
        control={control}
        error={errors.languageConfig?.supportedLanguages}
        options={availableDefaultLanguages}
        required
        multiple
      />
      <TextField
        error={!!errors.licenseInformation}
        helperText={errors.licenseInformation?.message}
        label='License Information'
        {...register('licenseInformation')}
        fullWidth
      />
      <PartnerSelect<MarketplaceConfigFormData>
        control={control}
        label='Default Registration Agent'
        name='defaultRegistrationAgentId'
        queryKey={['marketplace-default-registration-agent']}
        queryParams={{
          roleTypes: ['Agent'],
        }}
        error={errors.defaultRegistrationAgentId}
        multipleSelect={false}
      />
      <FormSelect
        label='Email Verification Flow'
        name='registrationFlow.emailVerification'
        control={control}
        error={errors.registrationFlow?.emailVerification}
        options={registrationOptions.emailVerificationOptions ?? []}
        required
      />
      <TextField
        error={!!errors.googleAnalyticsId}
        helperText={errors.googleAnalyticsId?.message}
        label='Google Analytics ID'
        {...register('googleAnalyticsId')}
        fullWidth
      />
      <TextField
        error={!!errors.openReplayProjectKey}
        helperText={errors.openReplayProjectKey?.message}
        label='OpenReplay Project Key'
        {...register('openReplayProjectKey')}
        fullWidth
      />

      <CollapsibleSection title='Sign Up Form Fields' showDivider>
        <Stack
          sx={{
            px: 1,
          }}
        >
          {sortedFieldOptions.map((option) => {
            const index = fields.findIndex((field) => field.name === option.id);
            const isIncluded = index !== -1;

            return (
              <Stack
                key={option.id}
                direction='row'
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormControlLabel
                  label={option.name}
                  sx={{ mr: 0 }}
                  slotProps={{ typography: { variant: 'body2' } }}
                  control={
                    <Checkbox
                      checked={isIncluded}
                      onChange={(e) => {
                        if (e.target.checked) {
                          append({
                            name: option.id,
                            order: option.order,
                            required: !!option.mandatory,
                          });
                        } else {
                          remove(index);
                        }
                      }}
                      disabled={!!option.mandatory}
                    />
                  }
                />
                {isIncluded && (
                  <Controller
                    name={`registrationFlow.signUpFormFields.${index}.required`}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        label='Req.'
                        slotProps={{ typography: { variant: 'body2' } }}
                        control={
                          <Checkbox checked={!!field.value} onChange={field.onChange} disabled={!!option.mandatory} />
                        }
                      />
                    )}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </CollapsibleSection>

      <CollapsibleSection title='Products'>
        <Stack spacing={1}>
          <FormFieldStack label='Sportsbook'>
            <Controller
              name='products.sportsbook'
              control={control}
              render={({ field }) => <Switch {...field} ref={null} />}
            />
          </FormFieldStack>
          <FormFieldStack label='Casino'>
            <Controller
              name='products.casino'
              control={control}
              render={({ field }) => <Switch {...field} ref={null} />}
            />
          </FormFieldStack>
        </Stack>
      </CollapsibleSection>

      <CollapsibleSection title='Features'>
        <Stack spacing={2}>
          <FormFieldStack label='Payments'>
            <Controller
              name='features.paymentSettings'
              control={control}
              render={({ field }) => <Switch {...field} ref={null} />}
            />
          </FormFieldStack>
          <FormFieldStack label='ComplyCube'>
            <Controller
              name='kyc.enabled'
              control={control}
              render={({ field }) => <Switch {...field} value={field.value ?? false} ref={null} />}
            />
          </FormFieldStack>
          {kycEnabled && (
            <FormSelect
              label='KYC Verification Flow'
              name='kyc.trigger'
              control={control}
              error={errors.kyc?.trigger}
              options={KYC_TRIGGER_OPTIONS}
              required
            />
          )}
        </Stack>
      </CollapsibleSection>

      <CollapsibleSection title='Maintenance'>
        <Stack spacing={2}>
          <FormFieldStack label='Under Maintenance'>
            <Controller
              name='maintenance.underMaintenance'
              control={control}
              render={({ field }) => <Switch {...field} value={field.value ?? false} ref={null} />}
            />
          </FormFieldStack>
          {!!supportedLanguages?.length && (
            <Stack spacing={1.5}>
              {supportedLanguages
                .filter((language): language is string => Boolean(language))
                .map((language) => (
                  <Controller
                    key={language}
                    name={`maintenance.message.${language}` as MessageFieldName}
                    control={control}
                    defaultValue=''
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={availableDefaultLanguages.find((option) => option.id === language)?.name ?? language}
                        fullWidth
                        multiline
                      />
                    )}
                  />
                ))}
            </Stack>
          )}
        </Stack>
      </CollapsibleSection>
      <CollapsibleSection title='Systems'>
        <FormFieldStack label='Affiliate'>
          <Controller
            name='systems.affiliate'
            control={control}
            render={({ field }) => <Switch {...field} ref={null} />}
          />
        </FormFieldStack>
      </CollapsibleSection>
    </Stack>
  );
};
const MarketplaceConfigTab = () => {
  return (
    <ConfigTabTemplate<MarketplaceConfigFormData>
      dataKey='marketplaceConfig'
      schema={marketplaceConfigSchema}
      defaultValues={DEFAULT_VALUES}
      resolveInitialValues={resolveInitialValues}
    >
      <MarketplaceConfigFields />
    </ConfigTabTemplate>
  );
};

export default MarketplaceConfigTab;
